import React from 'react';
import { useLanguage } from '../context/LanguageContext';

interface Props {
  page: number;
  pageSize: number;
  hasMore: boolean;
  total: number | null;
  maxPages: number;
  cursorMode?: boolean;
  onPageChange: (page: number) => void;
}

/** The page sentinel controls navigation; a background count only adds shortcuts. */
export default function SearchPagination({ page, pageSize, hasMore, total, maxPages, cursorMode = false, onPageChange }: Props) {
  const { t } = useLanguage();
  const canNext = hasMore && page + 1 < maxPages;
  if (page === 0 && !canNext) return null;

  const observedPages = page + 1 + Number(canNext);
  const reportedPages = total == null ? null : Math.ceil(total / pageSize);
  const pageCount = reportedPages == null || reportedPages < observedPages
    || (!hasMore && reportedPages > page + 1) ? null : Math.min(maxPages, reportedPages);
  const numbers = pageCount == null ? [] : Array.from(new Set([
    0, pageCount - 1, page - 1, page, page + 1,
  ])).filter((value) => value >= 0 && value < pageCount).sort((a, b) => a - b);
  const buttonClass = 'px-3 py-2 rounded-xl text-sm font-semibold text-goszakup-700 bg-goszakup-50 hover:bg-goszakup-100 disabled:opacity-40 disabled:cursor-not-allowed';

  return (
    <nav aria-label={t('lots.pagination.label')} className="flex items-center justify-center gap-2 sm:gap-4 mt-8">
      <button type="button" disabled={page === 0} onClick={() => onPageChange(page - 1)} className={buttonClass}>
        {t('tracker.pagePrev')}
      </button>
      <div className="hidden sm:flex items-center justify-center gap-1 min-w-[180px]">
        {pageCount == null || cursorMode ? (
          <span aria-current="page" className="text-sm text-gray-500 tabular-nums">{page + 1}{pageCount != null ? ` / ${pageCount}` : ''}</span>
        ) : numbers.map((number, index) => (
          <React.Fragment key={number}>
            {index > 0 && number > numbers[index - 1] + 1 && <span aria-hidden="true" className="px-1 text-gray-400">…</span>}
            <button type="button" aria-label={`${t('lots.pagination.page')} ${number + 1}`}
              aria-current={number === page ? 'page' : undefined}
              onClick={() => onPageChange(number)}
              className={`min-w-9 h-9 px-2 rounded-xl text-xs font-bold ${number === page ? 'bg-goszakup-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}>
              {number + 1}
            </button>
          </React.Fragment>
        ))}
      </div>
      <span className="sm:hidden text-sm text-gray-500 tabular-nums">{page + 1}{pageCount != null ? ` / ${pageCount}` : ''}</span>
      <button type="button" disabled={!canNext} onClick={() => onPageChange(page + 1)} className={buttonClass}>
        {t('tracker.pageNext')}
      </button>
    </nav>
  );
}
